// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

type GameScoringRuleResponse = {
  gameDefinitionId?: string;
  GameDefinitionId?: string;
  position?: number;
  Position?: number;
  points?: number;
  Points?: number;
};

export type ScoringPointsByGameId = Record<string, number[]>;

// Mirrors IsHazardGameName in the tour service's ApplyGameResultsCalculatedAppCommandHandler.
function isHazardGameName(name: string | undefined | null): boolean {
  if (!name) {
    return false;
  }
  const normalized = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  return (
    normalized.includes('water') ||
    normalized.includes('bunker') ||
    normalized.includes('penalty') ||
    normalized.includes('threeputt') ||
    normalized.includes('3putt') ||
    normalized.includes('hazard')
  );
}

// Mirrors ResolvePointsCalculationMode in the tour service: Challenge games (Mystery)
// have a single winner per event and no ranking — every event won pays the full
// first-place points for the field size.
export function isPerEventMeritGame(gameType: string | undefined | null): boolean {
  return (gameType ?? '').toLowerCase().includes('challenge');
}

// Merits one mystery-style event pays: the full first-place points for the field size,
// scaled by weight and rounded like the backend rounds each award.
export function getMeritPerEvent(
  scoringPointsByGameId: ScoringPointsByGameId,
  gameId: string,
  weightPercent: number,
): number | null {
  const basePoints = scoringPointsByGameId[gameId];
  if (!basePoints || basePoints.length === 0) {
    return null;
  }
  const multiplier = weightPercent <= 0 ? 0 : weightPercent / 100;
  return roundToNearestHalf(basePoints[0] * multiplier);
}

// Hazard event games (Water, Bunker, Penalty, 3 putts) penalize only the single loser:
// the player with the most hazards pays the first-place points with the weight as the
// share (4 players at 25% => -1 merit). Everyone else gets nothing.
export function isHazardMeritGame(
  name: string | undefined | null,
  gameType: string | undefined | null,
): boolean {
  return (gameType ?? '').toLowerCase().includes('event') && isHazardGameName(name);
}

// The (negative) merit the hazard loser pays, mirroring the backend's rounding.
export function getHazardLoserMerit(
  scoringPointsByGameId: ScoringPointsByGameId,
  gameId: string,
  weightPercent: number,
): number | null {
  const perEvent = getMeritPerEvent(scoringPointsByGameId, gameId, weightPercent);
  return perEvent === null ? null : -perEvent;
}

// Mirrors ResolvePointsCalculationMode in the tour service: "Achievement Game" types and
// non-hazard Event counters (Chip in) earn merits per achievement, not by placement ladder.
export function isAchievementMeritGame(
  name: string | undefined | null,
  gameType: string | undefined | null,
): boolean {
  const type = (gameType ?? '').toLowerCase();
  if (type.includes('achievement')) {
    return true;
  }
  return type.includes('event') && !isHazardGameName(name);
}

// Merits a single achievement (e.g. one chip-in) pays: the first-place points for the field
// size with the game weight as the winner's share (4 players at 25% => 1 merit), rounded
// like the backend rounds each award.
export function getMeritPerAchievement(
  scoringPointsByGameId: ScoringPointsByGameId,
  gameId: string,
  weightPercent: number,
): number | null {
  return getMeritPerEvent(scoringPointsByGameId, gameId, weightPercent);
}

export function getMeritLadder(
  scoringPointsByGameId: ScoringPointsByGameId,
  gameId: string,
  weightPercent: number,
): number[] {
  const basePoints = scoringPointsByGameId[gameId];
  if (!basePoints || basePoints.length === 0) {
    return [];
  }
  const multiplier = weightPercent <= 0 ? 0 : weightPercent / 100;
  return basePoints.map((points) => roundToNearestHalf(points * multiplier));
}

// Ladder to show in game pickers: achievement and per-event games display their single
// per-achievement/per-event award instead of a placement ladder, every other game shows
// its position ladder.
export function getDisplayMeritLadder(
  scoringPointsByGameId: ScoringPointsByGameId,
  gameId: string,
  weightPercent: number,
  name: string | undefined | null,
  gameType: string | undefined | null,
): number[] {
  if (isPerEventMeritGame(gameType)) {
    const perEvent = getMeritPerEvent(scoringPointsByGameId, gameId, weightPercent);
    return perEvent === null ? [] : [perEvent];
  }
  if (isHazardMeritGame(name, gameType)) {
    const loserMerit = getHazardLoserMerit(scoringPointsByGameId, gameId, weightPercent);
    return loserMerit === null ? [] : [loserMerit];
  }
  if (isAchievementMeritGame(name, gameType)) {
    const perAchievement = getMeritPerAchievement(scoringPointsByGameId, gameId, weightPercent);
    return perAchievement === null ? [] : [perAchievement];
  }
  return getMeritLadder(scoringPointsByGameId, gameId, weightPercent);
}

export function formatMerits(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function formatMeritLadder(ladder: number[], separator = ' / '): string {
  return ladder.map(formatMerits).join(separator);
}

// Mirrors ScoringRuleFormulaService.RoundToNearestHalf (midpoint away from zero).
function roundToNearestHalf(value: number): number {
  return Math.round(value * 2) / 2;
}

export function useGameScoringRules(
  apiBaseUrl: string,
  token: string | null | undefined,
  playerCount: number,
): ScoringPointsByGameId {
  const [scoringPointsByGameId, setScoringPointsByGameId] = useState<ScoringPointsByGameId>({});

  useEffect(() => {
    if (!token || playerCount <= 0) {
      setScoringPointsByGameId({});
      return;
    }

    let cancelled = false;
    const fetchRules = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/game-scoring-rules?playerCount=${playerCount}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          if (!cancelled) setScoringPointsByGameId({});
          return;
        }
        const payload = (await response.json()) as GameScoringRuleResponse[];
        if (cancelled) return;
        const entriesByGameId: Record<string, Array<{ position: number; points: number }>> = {};
        (payload ?? []).forEach((rule) => {
          const gameDefinitionId = rule.gameDefinitionId ?? rule.GameDefinitionId;
          const position = rule.position ?? rule.Position;
          const points = rule.points ?? rule.Points;
          if (!gameDefinitionId || typeof position !== 'number' || typeof points !== 'number' || Number.isNaN(points)) {
            return;
          }
          (entriesByGameId[gameDefinitionId] ??= []).push({ position, points });
        });
        const next: ScoringPointsByGameId = {};
        Object.entries(entriesByGameId).forEach(([gameId, entries]) => {
          next[gameId] = entries.sort((a, b) => a.position - b.position).map((entry) => entry.points);
        });
        setScoringPointsByGameId(next);
      } catch {
        if (!cancelled) setScoringPointsByGameId({});
      }
    };

    fetchRules();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, playerCount, token]);

  return scoringPointsByGameId;
}
